import React, { useEffect, useState } from 'react';
import { trackApi, errMsg } from '../../lib/trackApi';
import StatusBadge from '../../components/track/StatusBadge';
import {
  C, btn, btnGhost, btnDanger, btnSm, input as inputStyle, select as selectStyle, card, labelStyle, fmtDateTime,
} from '../../components/track/trackUi';

const WEEKDAYS = [
  { v: 1, l: '一' }, { v: 2, l: '二' }, { v: 3, l: '三' }, { v: 4, l: '四' },
  { v: 5, l: '五' }, { v: 6, l: '六' }, { v: 0, l: '日' },
];

function Section({ title, children, right }) {
  return (
    <div style={{ ...card, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <strong style={{ fontSize: 15 }}>{title}</strong>
        <div style={{ marginLeft: 'auto' }}>{right}</div>
      </div>
      {children}
    </div>
  );
}

function draftFrom(b) {
  return {
    name: b.name,
    description: b.description || '',
    lineGroupId: b.lineGroupId || '',
    dailyNotifyEnabled: b.dailyNotifyEnabled,
    dailyNotifyHhmm: b.dailyNotifyHhmm || '09:00',
    dailyNotifyWeekdays: b.dailyNotifyWeekdays || [0, 1, 2, 3, 4, 5, 6],
    statuses: b.statuses.map((s) => ({ ...s })),
  };
}

export default function TrackBoardSettings({ board, onSaved }) {
  const [d, setD] = useState(() => draftFrom(board));
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => { setD(draftFrom(board)); }, [board]);
  useEffect(() => {
    trackApi.listLineGroups().then((r) => setGroups(r?.data || [])).catch(() => setGroups([]));
    trackApi.lineTemplate().then((r) => setTemplates(r?.data || [])).catch(() => {});
    trackApi.notifyLogs(board.id).then((r) => setLogs(r?.data || [])).catch(() => {});
  }, [board.id]);

  const set = (p) => setD((x) => ({ ...x, ...p }));
  const setStatus = (i, p) => set({ statuses: d.statuses.map((s, j) => (j === i ? { ...s, ...p } : s)) });
  const moveStatus = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= d.statuses.length) return;
    const next = [...d.statuses];
    [next[i], next[j]] = [next[j], next[i]];
    set({ statuses: next });
  };

  const run = async (fn, okMsg) => {
    setBusy(true);
    setError('');
    setMsg('');
    try {
      await fn();
      if (okMsg) setMsg(okMsg);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () => run(async () => {
    const statuses = d.statuses
      .filter((s) => s.label.trim())
      .map((s) => ({ ...s, key: s.key || `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}` }));
    if (!statuses.some((s) => !s.isDone)) throw new Error('至少需要一個「未完成」狀態');
    await trackApi.patchBoard(board.id, { ...d, statuses });
    await onSaved();
  }, '已儲存');

  const loadPreview = () => run(async () => {
    const r = await trackApi.notifyPreview(board.id);
    setPreview(r?.data || null);
  });

  const sendNow = () => {
    if (!window.confirm('立即發送今日未完成任務通知？')) return;
    run(async () => {
      const r = await trackApi.notifySendNow(board.id);
      const res = r?.data?.results || [];
      const failed = res.filter((x) => x.ok === false);
      if (r?.data?.skipped === 'no_tasks') setMsg('目前沒有需要通知的任務');
      else if (failed.length) setError(`部分發送失敗：${failed.map((x) => x.error).join('；')}`);
      else setMsg(`已發送 ${res.length} 則`);
      const l = await trackApi.notifyLogs(board.id);
      setLogs(l?.data || []);
    });
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => setMsg('已複製')).catch(() => {});
  };

  const groupName = (gid) => groups.find((g) => g.groupId === gid)?.groupName || gid;

  return (
    <div style={{ maxWidth: 860 }}>
      {error && <div style={{ color: C.danger, marginBottom: 8 }}>{error}</div>}
      {msg && <div style={{ color: C.ok, marginBottom: 8 }}>{msg}</div>}

      <Section title="基本資料">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
          <div>
            <label style={labelStyle}>看板名稱</label>
            <input style={{ ...inputStyle, width: '100%' }} value={d.name} onChange={(e) => set({ name: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>說明</label>
            <input style={{ ...inputStyle, width: '100%' }} value={d.description} onChange={(e) => set({ description: e.target.value })} />
          </div>
        </div>
      </Section>

      <Section title="狀態">
        {d.statuses.map((s, i) => (
          <div key={s.key || `new-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
            <input type="color" value={s.color} onChange={(e) => setStatus(i, { color: e.target.value })} style={{ width: 34, height: 28, padding: 0, border: 'none' }} />
            <input style={{ ...inputStyle, width: 140 }} value={s.label} onChange={(e) => setStatus(i, { label: e.target.value })} />
            <label style={{ fontSize: 13, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
              <input type="checkbox" checked={!!s.isDone} onChange={(e) => setStatus(i, { isDone: e.target.checked })} />
              視為結案
            </label>
            <StatusBadge status={s} small />
            <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={() => moveStatus(i, -1)}>↑</button>
            <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={() => moveStatus(i, 1)}>↓</button>
            <button type="button" style={{ ...btnDanger, ...btnSm }} onClick={() => set({ statuses: d.statuses.filter((_, j) => j !== i) })}>刪除</button>
          </div>
        ))}
        <button
          type="button"
          style={{ ...btnGhost, ...btnSm }}
          onClick={() => set({ statuses: [...d.statuses, { key: '', label: '', color: '#6b7280', isDone: false }] })}
        >
          ＋ 狀態
        </button>
      </Section>

      <Section title="LINE 每日通知">
        <div style={{ display: 'grid', gap: 10 }}>
          <div>
            <label style={labelStyle}>通知群組（也是可回覆公版的群組）</label>
            <select style={{ ...selectStyle, width: '100%' }} value={d.lineGroupId} onChange={(e) => set({ lineGroupId: e.target.value })}>
              <option value="">不綁定群組</option>
              {d.lineGroupId && !groups.some((g) => g.groupId === d.lineGroupId) && <option value={d.lineGroupId}>{d.lineGroupId}</option>}
              {groups.map((g) => <option key={g.groupId} value={g.groupId}>{g.groupName || g.groupId}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
            <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
              <input type="checkbox" checked={d.dailyNotifyEnabled} onChange={(e) => set({ dailyNotifyEnabled: e.target.checked })} />
              啟用每日通知
            </label>
            <input type="time" style={inputStyle} value={d.dailyNotifyHhmm} onChange={(e) => set({ dailyNotifyHhmm: e.target.value })} />
            <span>
              {WEEKDAYS.map((w) => (
                <label key={w.v} style={{ marginRight: 6 }}>
                  <input
                    type="checkbox"
                    checked={d.dailyNotifyWeekdays.includes(w.v)}
                    onChange={(e) => set({
                      dailyNotifyWeekdays: e.target.checked
                        ? [...d.dailyNotifyWeekdays, w.v]
                        : d.dailyNotifyWeekdays.filter((x) => x !== w.v),
                    })}
                  />
                  {w.l}
                </label>
              ))}
            </span>
          </div>
          <div style={{ fontSize: 12, color: C.muted }}>成員開啟「個人通知」且已綁定 LINE 者，另收到自己的待辦。</div>
        </div>
      </Section>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        <button type="button" style={btn} disabled={busy} onClick={save}>儲存設定</button>
        <button type="button" style={btnGhost} onClick={() => setD(draftFrom(board))}>還原</button>
      </div>

      <Section
        title="通知預覽"
        right={(
          <span style={{ display: 'flex', gap: 6 }}>
            <button type="button" style={{ ...btnGhost, ...btnSm }} disabled={busy} onClick={loadPreview}>預覽</button>
            <button type="button" style={{ ...btn, ...btnSm }} disabled={busy} onClick={sendNow}>立即發送</button>
          </span>
        )}
      >
        {preview ? (
          <>
            <div style={{ fontSize: 13, marginBottom: 6 }}>
              需通知 {preview.taskCount} 件；收件：{(preview.results || []).map((r) => (r.kind.includes('group') ? '群組' : '個人')).join('、') || '無'}
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, fontSize: 13, margin: 0 }}>
              {preview.groupText || '（沒有需要通知的任務）'}
            </pre>
          </>
        ) : <div style={{ fontSize: 13, color: C.muted }}>點「預覽」查看今日訊息內容</div>}
      </Section>

      <Section title="LINE 回覆公版">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          {templates.map((t) => (
            <div key={t.key}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <strong style={{ fontSize: 13 }}>{t.label}</strong>
                <button type="button" style={{ ...btnGhost, ...btnSm, marginLeft: 'auto' }} onClick={() => copy(t.text)}>複製</button>
              </div>
              <pre style={{ whiteSpace: 'pre-wrap', background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: 6, padding: 8, fontSize: 12, margin: 0 }}>{t.text}</pre>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>在群組或私訊官方帳號傳「公版」可取得格式；僅已綁定的成員可操作。</div>
      </Section>

      <Section title="發送紀錄">
        {!logs.length && <div style={{ fontSize: 13, color: C.muted }}>尚無紀錄</div>}
        {logs.slice(0, 30).map((l) => (
          <div key={l.id} style={{ display: 'flex', gap: 10, fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ color: C.muted, width: 110 }}>{fmtDateTime(l.sentAt)}</span>
            <span style={{ width: 70 }}>{l.kind.includes('group') ? '群組' : '個人'}{l.kind.startsWith('manual') ? '・手動' : ''}</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.payload}>
              {l.kind.includes('group') ? groupName(l.target) : ''} {(l.payload || '').split('\n')[0]}
            </span>
            <span style={{ color: l.error ? C.danger : C.ok }}>{l.error ? '失敗' : (l.messageId ? 'OK' : '—')}</span>
          </div>
        ))}
      </Section>
    </div>
  );
}
